import create from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  getNotification,
  getUnreadNotificationNumber,
  markAsRead,
} from 'lib/api/notification';
import { notificationTabsAttrs } from 'utils/notification';

import { NotificationItem, NotificationType } from './slice.types';

type NotificationContent = Record<
  NotificationType,
  {
    list: NotificationItem[];
    lastId: number | null;
  }
>;

interface State {
  tab: NotificationType;
  isPopupOpen: boolean;
  isConvoOpen: boolean;
  convoId?: number;

  isReading: boolean;
  isLoading: boolean;
  isLoadingPrevious: boolean;
  errorMessage?: string;
  contents: NotificationContent;
  unread: number;
}

interface Actions {
  changeTab(tab: NotificationType): void;
  togglePopup(): void;
  closePopup(): void;
  setConvoOpen(id: number, isOpen: boolean): void;
  closeConvo(): void;

  setUnread(count: number): void;

  getNotification(tab?: NotificationType, lastId?: number): void;
  getPrevNotification(tab?: NotificationType, lastId?: number): void;
  getUnreadCount(): void;
  markNotifAsRead(tab?: NotificationType): void;
}

export const useNotificationData = create(
  immer<State & Actions>((set) => ({
    tab: 'notification',
    isPopupOpen: false,
    isConvoOpen: false,

    isReading: false,
    isLoading: false,
    isLoadingPrevious: false,
    contents: {
      notification: {
        list: [],
        lastId: null,
      },
      mention: {
        list: [],
        lastId: null,
      },
      report: {
        list: [],
        lastId: null,
      },
      tipping: {
        list: [],
        lastId: null,
      },
    },
    unread: 0,

    // UI Reducers
    changeTab(tab) {
      set((draft) => {
        draft.tab = tab;
      });
    },

    togglePopup() {
      set((draft) => {
        draft.isPopupOpen = !draft.isPopupOpen;
      });
    },

    closePopup() {
      set((draft) => {
        draft.isPopupOpen = false;
      });
    },

    setConvoOpen(id, isOpen = false) {
      set((draft) => {
        draft.isConvoOpen = isOpen;
        draft.convoId = id;
      });
    },

    closeConvo() {
      set((draft) => {
        draft.isConvoOpen = false;
        draft.convoId = undefined;
      });
    },

    setUnread(count: number) {
      set((draft) => {
        draft.unread = count;
      });
    },

    // Asynchronous actions
    async getNotification(tab?: NotificationType) {
      try {
        set((draft) => {
          draft.isLoading = true;
        });

        const typeParams = notificationTabsAttrs.find(
          (item) => item.name === tab,
        );

        const { data, status } = await getNotification(typeParams.typeCodes);
        if (status !== 200) {
          throw new Error(data.error || data.error_type);
        }
        const {
          data: { result, unread },
        } = data;

        set((draft) => {
          draft.unread = unread;
          draft.contents[tab].list = result;
          draft.isLoading = false;
          if (result.length - 1 >= 0) {
            draft.contents[tab].lastId = result[result.length - 1].id;
          }
        });
      } catch (error) {
        set((draft) => {
          draft.errorMessage = error.message;
          draft.isLoading = false;
        });
      }
    },

    async getPrevNotification(tab?: NotificationType, lastId?: number) {
      // if previous
      set((draft) => {
        draft.isLoadingPrevious = true;
      });

      const typeParams = notificationTabsAttrs.find(
        (item) => item.name === tab,
      );

      const { data, status } = await getNotification(
        typeParams.typeCodes,
        25,
        lastId,
      );
      if (status !== 200) {
        throw new Error(data.error || data.error_type);
      }
      const {
        data: { result, unread },
      } = data;

      set((draft) => {
        draft.unread = unread;
        draft.contents[tab].list = [...draft.contents[tab].list, ...result];
        draft.isLoadingPrevious = false;
        if (result.length - 1 >= 0) {
          draft.contents[tab].lastId = result[result.length - 1].id;
        }
      });
    },

    async getUnreadCount() {
      try {
        set((draft) => {
          draft.errorMessage = undefined;
        });

        const { data, status } = await getUnreadNotificationNumber();
        if (status !== 200) {
          throw new Error(data.error || data.error_type);
        }
        const { data: result } = data;

        if (result) {
          set((draft) => {
            draft.unread = result.unread;
          });
        } else {
          throw new Error('failed to read notification');
        }
      } catch (error) {
        set((draft) => {
          draft.errorMessage = error.message;
        });
      }
    },

    async markNotifAsRead(tab = 'notification') {
      try {
        set((draft) => {
          draft.errorMessage = undefined;
          draft.isReading = true;
        });
        const typeParams = notificationTabsAttrs.find(
          (item) => item.name === tab,
        );

        const { data, status } = await markAsRead(typeParams.typeCodes);
        if (status !== 200) {
          throw new Error(data.error || data.error_type);
        }
        const { data: result } = data;
        if (result) {
          set((draft) => {
            draft.unread = 0;
            draft.isReading = false;
            draft.contents[tab].list = draft.contents[tab].list.map((item) => ({
              ...item,
              is_read: true,
            }));
          });
        } else {
          throw new Error('failed to read notification');
        }
      } catch (error) {
        set((draft) => {
          draft.errorMessage = error.message;
          draft.isReading = false;
        });
      }
    },
  })),
);
